import _ from 'lodash';

import { Get, urls } from 'assets/js/api';

// checkErrorName checks to see if an error name is a 401 and displays a
// warning message if it is. Otherwise, it's unhandled.
const checkErrorName = (err, authErrorStr) => {
  if (err.name === 'StatusCode401' || _.includes(err.message,
    'AUTHORIZATION_ERROR')) {
    console.warn(`Memberships store auth error: ${authErrorStr}`);
  } else {
    throw Error(err);
  }
};

const state = {
  loaded: false,
  memberships: [],
  promiseResolvers: [],
};

// fetch fetches the list of memberships to sites.
const fetch = async (commit, override) => {
  let resp;
  try {
    resp = await Get(urls.MEMBERSHIPS);
  } catch (err) {
    checkErrorName(err, 'Failed to fetch memberships do to an issue with the ' +
      'login token');
    return [];
  }

  // The optional override argument is a site ID.
  if (override) {
    let siteResp;
    try {
      siteResp = await Get(urls.SITE(override));
    } catch (err) {
      checkErrorName(err,
        'Failed to fetch membership do to an issue with the ' +
          `login token (dev override site ID: ${override})`);
      return [];
    }

    siteResp.data.injectedForPortal = true;
    resp.data.push(siteResp.data);
  }

  commit(resp.data);
  return resp.data;
};

// byName is a sort function that case insensitively sorts Objects by
// their name property.
const byName = (a, b) => {
  const nA = a.name.toLowerCase();
  const nB = b.name.toLowerCase();
  if (nA < nB) return -1;
  if (nA > nB) return 1;
  return 0;
};

const actions = {
  // DropMembership removes a given membership by ID. If the ID does not exist,
  // an error is thrown.
  DropMembership({ commit }, membershipId) {
    commit('DROP_MEMBERSHIP', membershipId);
  },

  // FetchMemberships returns a promise that resolves to the value fetched.
  // If a caller calls the function while the store is currently in a fetching
  // state (as indicated by the length of the resolvers being more than 0 when
  // the function was originally called), then just return a promise that
  // resolves when the original promise resolves that also contains the value
  // of the original promise's resolution. This enabled concurrent callers to
  // not fetch more than necessary.
  //
  // The optional ID 'override' allows you to inject a single site into the
  // list of memberships. This is used by a developer or a portal admin to
  // administer sites they are not a member of.
  FetchMemberships({ commit }, override) {
    // Spin up a promise that resolves when the root promise spawned below
    // resolves.
    let resolver;
    const promiseFinished = new Promise((resolve) => {
      resolver = resolve;
    });
    commit('SET_MEMBERSHIP_PROMISE_RESOLVER', resolver);

    // If we have more than 1 promise on the stack, then we've already got a
    // pull down in progress. Just return the promise awaiting the original
    // call to finish.
    if (state.promiseResolvers.length > 1) return promiseFinished;

    // We only have a single promise resolver on the stack, so spin up a
    // function that pulls down the data from the API.
    const promiseGenerator = async () => {
      await fetch((sites) => commit('SET_MEMBERSHIPS', sites), override);

      // The pull down is done, so we can resolve our listeners to the newly
      // set value in the store.
      _.forEach(state.promiseResolvers, (r) => r(state.memberships));

      // Reset the set of the store to indicate that an API call is currently
      // not in progress.
      commit('SET_MEMBERSHIP_PROMISE_RESOLVER', 'reset');
    };

    // Start the root promise that actually does the grunt work running.
    promiseGenerator();

    // Return the promise that awaits the results of the pull down.
    return promiseFinished;
  },

  // MembershipsReset resets the memberships state.
  MembershipsReset({ commit }) {
    commit('RESET_MEMBERSHIPS_STATE');
  },

  // MembershipsUpdate takes an array of site memberships and either
  // inserts them into the store or modifies their entries if they already
  // exist.
  MembershipsUpdate({ commit }, sites) {
    commit('SET_MEMBERSHIPS', sites);
  },
};

const getters = {
  // membershipsTheme returns the theme to be used for the UI (e.g. for white-
  // labelling purposes). If all of the user's memberships have the same theme,
  // this returns that theme. Otherwise, it returns null.
  membershipsTheme: (stateArg) => {
    const eligibleThemes = stateArg.memberships.map((m) => m.theme);

    // If any membership has no theme associated, return default theming (null).
    const themelessSites = _.filter(eligibleThemes, (theme) => !theme);
    if (themelessSites && themelessSites.length) return null;

    // If all sites have the same theme, return it. Otherwise, null.
    const uniqueThemes = _.uniqBy(eligibleThemes, 'theme_id');

    if (uniqueThemes.length && uniqueThemes.length === 1) {
      return eligibleThemes[0];
    }
    return null;
  },

  // sitesWithProjects is a list of sites that allow users to make projects.
  sitesWithProjects: (stateArg) =>
    _(stateArg.memberships).filter((m) => m.has_projects)
      .map((m) => m.site_id)
      .value(),
};

const mutations = {
  // DROP_MEMBERSHIP deletes a membership from the state's memberships by ID.
  DROP_MEMBERSHIP(stateArg, membershipId) {
    const stateCopy = stateArg;
    const idx = _.findIndex(stateCopy.memberships, (m) =>
      m.site_id === membershipId);
    if (idx === -1) {
      throw Error(`missing membership id ${membershipId} to drop`);
    } else {
      stateCopy.memberships.splice(idx, 1);
    }
  },

  // RESET_MEMBERSHIPS_STATE resets the memberships state.
  RESET_MEMBERSHIPS_STATE(stateArg) {
    const stateCopy = stateArg;
    stateCopy.loaded = false;
    stateCopy.memberships = [];
    stateCopy.promiseResolvers = [];
  },

  // SET_MEMBERSHIP_PROMISE_RESOLVER adds a call to the list of simultaneous
  // callers.
  SET_MEMBERSHIP_PROMISE_RESOLVER(stateArg, resolver) {
    const stateCopy = stateArg;
    if (resolver === 'reset') {
      stateCopy.promiseResolvers = [];
      return;
    }

    stateCopy.promiseResolvers.push(resolver);
  },

  // SET_MEMBERSHIPS sets the memberships state.
  SET_MEMBERSHIPS(stateArg, memberships) {
    const stateCopy = stateArg;
    _.forEach(memberships, (site) => {
      const idx = _.findIndex(stateCopy.memberships, (m) =>
        m.site_id === site.site_id);
      if (idx === -1) {
        stateCopy.memberships = [_.cloneDeep(site), ...stateCopy.memberships];
      } else {
        stateCopy.memberships[idx] = site;
        stateCopy.memberships = _.clone(stateCopy.memberships);
      }
    });
    stateCopy.memberships.sort(byName);
    stateCopy.loaded = true;
  },
};

export default { actions, getters, mutations, state };
